import { Flex, Spinner } from "@chakra-ui/react";
import useCheckInspectionType from "components/containers/hooks/useCheckInspectionType";
import { useQuery } from "hooks/useQuery";
import { isEqual } from "lodash-es";
import { getPinsOfGroup } from "models/inspection";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { fetchPins, resetPins } from "redux/pinSlice";
import { RootState } from "redux/store";
import { routePath } from "routes/path";
import { InspectionGroupService } from "services/inspectionGroup";
import { logDev } from "utils/logs";
import useLoadForgeViewer from "../hooks/useLoadForgeViewer";
import {
  DETAIL_MODE,
  DETAIL_TAB,
  initialInspectionDetail,
  InspectionDetail,
  InspectionDetailActions,
  InspectionDetailReducer,
  Types,
} from "./inspectionDetailReducer";
import useInspectionDetail from "./useDetail";

// Define the types for the props and the state
type Props = PropsWithChildren<{}>;

// Create two contexts: one for the state (TaskTypesContext) and one for the dispatch function (TaskTypesDispatchContext)
const InspectionDetailContext = createContext<any>({
  ...initialInspectionDetail,
});

const InspectionDetailDispatchContext = createContext(
  (() => {}) as React.Dispatch<InspectionDetailActions>
);

export type QueryUrlInspectionDetail = {
  mode?: DETAIL_MODE;
  documentId?: string;
  pinGroupId?: string;
  pinEditId?: string;
  step?: string;
};

export function InspectionDetailLoader({ children }: Props) {
  const { isLoading } = useInspectionDetail();
  useLoadForgeViewer();

  if (isLoading) {
    return (
      <Flex mt="2rem" justifyContent="center">
        <Spinner />
      </Flex>
    );
  }

  return <>{children}</>;
}

// Define the provider component
export function InspectionDetailProvider({ children }: Props) {
  const query: QueryUrlInspectionDetail = useQuery();
  const pinGroupId = query?.pinGroupId;
  const location = useLocation();
  const { id: inspectionTypeId, projectId, bimId } = useParams();

  const { pins, isFetchedPins, filters } = useSelector(
    (state: RootState) => state.pin
  );
  const { isSyncOfflineData } = useSelector((state: RootState) => state.app);

  // @ts-ignore
  const [inspectionDetail, dispatch] = useReducer(InspectionDetailReducer, {
    ...initialInspectionDetail,
    mode: query.mode ? Number(query.mode) : initialInspectionDetail.mode,
  });

  const mode = inspectionDetail.mode;
  const isTabDocument = inspectionDetail.detailTabIndex === DETAIL_TAB.DOCUMENT;

  const navigate = useNavigate();
  const dispatchRedux = useDispatch();

  useCheckInspectionType(inspectionDetail?.inspectionType);
  // fetch pins by inspectionTypeId (ir add pin exists mode)
  useEffect(() => {
    if (isSyncOfflineData) {
      return;
    }

    if (mode === DETAIL_MODE.ADD_PIN_EXIST && inspectionTypeId) {
      dispatchRedux(fetchPins({ inspectionTypeId }));
    }
  }, [mode, isTabDocument, inspectionTypeId, isSyncOfflineData, dispatchRedux]);

  // fetch pins by group (in normal mode)
  useEffect(
    () => {
      const pinGroupId = inspectionDetail?.inspectionGroup?.id;

      if (!pinGroupId || mode === DETAIL_MODE.ADD_PIN_EXIST || isFetchedPins) {
        return;
      }

      if (mode === DETAIL_MODE.ALL && isTabDocument) {
        return;
      }

      dispatchRedux(fetchPins({ inspectionGroupId: pinGroupId }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mode,
      isTabDocument,
      isSyncOfflineData,
      isFetchedPins,
      inspectionDetail?.inspectionGroup?.id,
    ]
  );

  useEffect(() => {
    // reset pins when sync data from offline to online
    if (!isSyncOfflineData) {
      dispatchRedux(resetPins());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSyncOfflineData]);

  // filter pins by groupId
  const pinsOfGroup = useMemo(() => {
    const result = inspectionDetail.inspectionGroup?.id
      ? getPinsOfGroup(pins, inspectionDetail.inspectionGroup?.id)
      : [];

    return result;
  }, [pins, inspectionDetail?.inspectionGroup?.id]);

  const pinOfGroupIds = useMemo(
    () => new Set(pinsOfGroup.map((p) => p.id)),
    [pinsOfGroup]
  );

  const currentInspectionPins = useMemo(() => {
    const searchLowerCase = filters?.search?.trim()?.toLowerCase();
    if (!searchLowerCase) {
      return pinsOfGroup;
    }

    return pinsOfGroup.filter((pin) => {
      return pin.label.toLowerCase().includes(searchLowerCase);
    });
  }, [filters?.search, pinsOfGroup]);

  useEffect(() => {
    return () => {
      dispatchRedux(resetPins());
      dispatch({
        type: Types.SET_TARGET_OBJECTS,
        payload: [],
      });
    };
  }, [dispatchRedux]);

  const paramsGetInspectionGroup = useRef<{
    currentGroupId?: string;
    pinGroupId?: string;
    isSyncOfflineData?: boolean;
  }>({});
  const currentGroupId = inspectionDetail.inspectionGroup?.id;

  const pinsByMode = useMemo(() => {
    if (mode === DETAIL_MODE.ADD_PIN_EXIST) {
      const pinOthers = pins.filter((pin) => {
        const isNotCurrentGroup = !pin.pinGroups?.find(
          (p) => p.pinGroupId === currentGroupId
        );

        if (inspectionDetail.isFilterPinNoGroup) {
          return isNotCurrentGroup && !pin?.pinGroups?.length;
        }

        return isNotCurrentGroup;
      });

      return [...currentInspectionPins, ...pinOthers];
    }

    return currentInspectionPins;
  }, [
    currentGroupId,
    currentInspectionPins,
    inspectionDetail.isFilterPinNoGroup,
    mode,
    pins,
  ]);

  const currentInspectionPinIds = useMemo(
    () => new Set(currentInspectionPins.map((p) => p.id)),
    [currentInspectionPins]
  );

  useEffect(() => {
    if ((currentGroupId && !isSyncOfflineData) || !pinGroupId) {
      return;
    }
    const params = {
      currentGroupId,
      pinGroupId,
      isSyncOfflineData,
    };
    const prevParams = paramsGetInspectionGroup.current;
    if (isEqual(prevParams, params)) {
      return;
    }

    (async () => {
      try {
        const result = await InspectionGroupService.getInspectionGroupById(
          pinGroupId
        );
        paramsGetInspectionGroup.current = params;

        if (result.data?.id) {
          dispatch({
            type: Types.SET_SELECTED_INSPECTION_GROUP,
            payload: result?.data,
          });
        } else {
          navigate(
            generatePath(routePath.InspectionDetail, {
              projectId,
              bimId,
              id: inspectionTypeId,
            })
          );
          dispatch({
            type: Types.COMBINE_STATE,
            payload: {
              inspectionGroup: undefined,
              mode: DETAIL_MODE.ALL,
            },
          });
        }
      } catch (error) {
        logDev(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId, pinGroupId, isSyncOfflineData]);

  // Keep MODE, Pin group when refresh page and navigation
  useEffect(
    () => {
      const params = new URLSearchParams(location.search);
      const mode = inspectionDetail.mode;
      params.set("mode", mode.toString());

      if (currentGroupId) {
        params.set("pinGroupId", currentGroupId);
      } else {
        params.delete("pinGroupId");
      }
      const pinEditId = inspectionDetail?.pinEditId;
      if (pinEditId) {
        params.set("pinEditId", pinEditId);
      } else {
        params.delete("pinEditId");
      }
      const newUrl = `${location.pathname}?${params.toString()}`;
      navigate(newUrl);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      inspectionDetail?.pinEditId,
      location.pathname,
      location.search,
      inspectionDetail?.mode,
      currentGroupId,
    ]
  );

  return (
    <InspectionDetailContext.Provider
      value={{
        ...inspectionDetail,
        pinsOfGroup,
        currentInspectionPins,
        pinsByMode,
        pinOfGroupIds,
        currentInspectionPinIds,
      }}
    >
      <InspectionDetailDispatchContext.Provider value={dispatch}>
        <InspectionDetailLoader>{children}</InspectionDetailLoader>
      </InspectionDetailDispatchContext.Provider>
    </InspectionDetailContext.Provider>
  );
}

// Define hooks for accessing the state and the dispatch function
export function useContextInspectionDetail() {
  return useContext(InspectionDetailContext) as InspectionDetail;
}

export function useInspectionDetailDispatch() {
  return useContext(InspectionDetailDispatchContext);
}
